import camelcaseKeys from 'camelcase-keys';
import ky, { isHTTPError } from 'ky';
import snakecaseKeys from 'snakecase-keys';

import { getEnv } from '@/utils/env';

import { UUID_REGEX } from '../constants/uuid-regex';

interface ApiErrorResponseBody {
  error: Record<string, unknown>;
  message: string;
}

function isApiErrorResponseBody(data: unknown): data is ApiErrorResponseBody {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const { error, message } = data as ApiErrorResponseBody;
  return Boolean(error && message);
}

export class ServerError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`Internal server error (${status})`);
    this.name = 'ServerError';
    this.status = status;
  }
}

const SERVER_ERROR_STATUS = 500;

/**
 * Upstream drops a double-digit share of requests, so one attempt is not a verdict. Retries stay on
 * idempotent methods only — ky consults `methods` before anything else, so writes can never repeat.
 */
const RETRYABLE_METHODS = ['get', 'head', 'options'];
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];
const RETRY_LIMIT = 2;
/** Caps the added latency of a retried SSR render at ~1s of waiting. */
const RETRY_BACKOFF_LIMIT_MS = 1000;

export const baseFetch = ky.create({
  retry: {
    limit: RETRY_LIMIT,
    methods: RETRYABLE_METHODS,
    statusCodes: RETRYABLE_STATUS_CODES,
    backoffLimit: RETRY_BACKOFF_LIMIT_MS,
    // A hung request already cost its full timeout; repeating it is how a slow render becomes a dead one.
    retryOnTimeout: false,
  },
  prefix: `${getEnv('API_URL')}`,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  parseJson: (text) => {
    let parsedText = text;

    if (Object.keys(parsedText).length) {
      parsedText = camelcaseKeys(JSON.parse(parsedText), {
        deep: true,
        exclude: [UUID_REGEX],
        stopPaths: ['data.training_content', 'data.description_blocks', 'uid', 'data.answers.values'],
      });
    }

    return parsedText;
  },
  stringifyJson: (data) => {
    let parsedData = data;

    if (data && Object.keys(data).length > 0) {
      parsedData = snakecaseKeys(data as Record<string, unknown>, { exclude: ['data.answers.values'] });
    }

    return JSON.stringify(parsedData);
  },
  hooks: {
    // Throwing from `afterResponse` skips ky's retry entirely, so 5xx becomes a `ServerError` here
    // instead — after the retries are spent.
    beforeError: [
      ({ error }) => {
        if (!isHTTPError(error)) {
          return error;
        }

        if (error.response.status >= SERVER_ERROR_STATUS) {
          return new ServerError(error.response.status);
        }

        if (isApiErrorResponseBody(error.data)) {
          error.name = error.name || 'Error';
          error.message = error.data.message;
        }

        return error;
      },
    ],
  },
});
